import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import Hero from '../../components/Hero/Hero';
import Section from '../../components/Section/Section';
import Metadata from '../../components/Metadata/Metadata';
import Button from '../../components/Button/Button';
import Card from '../../components/Card/Card';
import ListItem from '../../components/ListItem/ListItem';
import Accordion from '../../components/Accordion/Accordion';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import './curosygnal.scss';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box className="curosygnal-page">
      <Metadata
        title="CuroSygnal™ | Explainable AI for Return-to-Hospital Risk"
        description="CuroSygnal™ is an explainable AI risk platform for skilled nursing and post-acute care teams, surfacing return-to-hospital risk signals 3 to 14 days in advance."
      />
      <Hero
        helper="CuroSygnal™ · Curo46®"
        headline="See return-to-hospital risk before it escalates"
      >
        <p className="curosygnal-hero-copy action-on-view action-fade-in action-delay-500">
          CuroSygnal™ surfaces residents with elevated risk indicators 3 to 14
          days before a potential admission, giving nurses a prioritized list to
          review with the contributing clinical signals behind every score.
        </p>
        <div className="curosygnal-hero-actions action-on-view action-fade-in action-delay-500">
          <Button
            url="https://ggi.cal.com/curosygnal/curosygnal-30-minute-demo"
            label="Book a 30-minute demo"
            icon="calendar_month"
          />
        </div>
      </Hero>

      <Section className="curosygnal-dashboard-section">
        <div className="curosygnal-dashboard-frame action-on-view action-fade-in">
          <img
            src="/images/library/curosygnal-dashboard.png"
            alt="CuroSygnal™ dashboard showing anonymized demo telemetry, risk score trends, and residents at risk."
          />
        </div>
        <div className="curosygnal-metrics">
          <div className="curosygnal-metric action-on-view action-fade-in">
            <p className="metric-helper">Up to</p>
            <p className="metric-value">98%</p>
            <p className="metric-label">
              Accuracy on return-to-hospital predictions made within a 24h
              window, up to 2 weeks in advance.
            </p>
          </div>
          <div className="curosygnal-metric action-on-view action-fade-in action-delay-250">
            <p className="metric-helper">Up to</p>
            <p className="metric-value">98.5%</p>
            <p className="metric-label">
              AUC, measuring threshold-independent discrimination.
            </p>
          </div>
          <div className="curosygnal-metric action-on-view action-fade-in action-delay-500">
            <p className="metric-helper">Up to</p>
            <p className="metric-value">98.7%</p>
            <p className="metric-label">
              Sensitivity at the best operating threshold.
            </p>
          </div>
        </div>
        <p className="curosygnal-footnote action-on-view action-fade-in">
          These figures reflect validation across multi-facility post-acute
          deployments. AUC is the model&apos;s ability to correctly rank
          patients by return-to-hospital risk.
        </p>
      </Section>

      <Section className="curosygnal-explain-section">
        <div className="curosygnal-explain-wrapper">
          <div className="curosygnal-explain-content">
            <p className="helper action-on-view action-fade-in">
              Clinical risk intelligence
            </p>
            <h2 className="action-on-view action-fade-in">
              Built to reduce alert noise and support clinician review
            </h2>
            <p className="action-on-view action-fade-in">
              Rehospitalizations drive Five-Star penalties, value-based
              purchasing risk, and post-acute margin compression. Many of them
              are predictable. CuroSygnal™ helps care teams see the signals
              earlier without adding another layer of alert noise.
            </p>
            <ListItem
              icon="check"
              iconClass="highlighted-icon"
              className="custom-list-item"
            >
              <p>
                <strong>High signal-to-noise:</strong> a short, prioritized list
                of residents to review today.
              </p>
            </ListItem>
            <ListItem
              icon="check"
              iconClass="highlighted-icon"
              className="custom-list-item"
            >
              <p>
                <strong>Feature-level explainability:</strong> every score is
                traceable to the contributing clinical signals.
              </p>
            </ListItem>
            <ListItem
              icon="check"
              iconClass="highlighted-icon"
              className="custom-list-item"
            >
              <p>
                <strong>Clinician-directed decisions:</strong> CuroSygnal™
                supports clinical judgment; final decisions stay with the care
                team.
              </p>
            </ListItem>
          </div>
          <div className="curosygnal-explain-visual action-on-view action-fade-in action-delay-250">
            <img
              src="/images/library/curosygnal-dashboard.png"
              alt="CuroSygnal™ dashboard showing anonymized residents, risk trends, and facility metrics."
            />
          </div>
        </div>
      </Section>

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">What your team gets</h2>
        <p className="action-on-view action-fade-in">
          Core platform features for single-facility teams and multi-facility
          post-acute networks.
        </p>
        <Box className="card-group">
          <Card
            icon="format_list_numbered"
            headline="Hot Rack ranked patient queue"
            subhead="Prioritized list of residents with elevated risk indicators, with contributing signals visible on every score."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="query_stats"
            headline="Trend forecasting"
            subhead="Surfaces the triggering observations behind a rising risk score so clinicians can interpret the signal in context."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="admin_panel_settings"
            headline="Operational views and access"
            subhead="Census, payer, role-based, facility-scoped, region-scoped, and executive views governed by your identity tenant."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <Section theme="dark" bgName="lines">
        <Typography variant="h2" className="action-on-view action-fade-in">
          In practice
        </Typography>
        <div className="curosygnal-workflow action-on-view action-fade-in">
          <p>
            When a resident&apos;s risk score crosses threshold, the day-shift
            charge nurse opens Hot Rack, reviews the contributing clinical
            signals, and brings the case to the morning huddle days before a
            potential admission.
          </p>
        </div>
        <div className="curosygnal-proof-grid">
          <div className="curosygnal-proof action-on-view action-fade-in">
            <h3>3 to 14 day window</h3>
            <p>
              Lead time to review emerging risk signals before a potential
              return-to-hospital event.
            </p>
          </div>
          <div className="curosygnal-proof action-on-view action-fade-in action-delay-250">
            <h3>Native to MatrixCare</h3>
            <p>
              First-class integration with MatrixCare, with integration
              available for other EHR platforms.
            </p>
          </div>
          <div className="curosygnal-proof action-on-view action-fade-in action-delay-500">
            <h3>Microsoft sign-in</h3>
            <p>
              Authenticate with your Microsoft identity. Audit trail included.
            </p>
          </div>
        </div>
      </Section>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Built for teams that carry rehospitalization risk
        </Typography>
        <div className="curosygnal-audience-grid">
          <div className="curosygnal-audience action-on-view action-fade-in">
            <h3>Skilled nursing networks</h3>
            <p>
              Prioritize resident review across facilities with live operational
              visibility.
            </p>
          </div>
          <div className="curosygnal-audience action-on-view action-fade-in action-delay-250">
            <h3>Post-acute operators</h3>
            <p>
              See census, payer, authorization, and risk signals in the same
              operating context.
            </p>
          </div>
          <div className="curosygnal-audience action-on-view action-fade-in action-delay-500">
            <h3>Acute discharge teams</h3>
            <p>
              Support post-discharge coordination with forward-looking risk
              intelligence.
            </p>
          </div>
        </div>
      </Section>

      <Section theme="dark" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Privacy, security, and regulatory posture
        </Typography>
        <Box className="card-group">
          <Card
            icon="verified_user"
            headline="Clinical decision support"
            subhead="CuroSygnal™ is positioned under the FDA Clinical Decision Support exemption. It does not diagnose, recommend treatment, dose medication, or autonomously alter care plans."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="lock"
            headline="No PHI in the sales demo"
            subhead="Demos run on Curo46-hosted fake patients and facilities. No PHI is exchanged in the sales cycle."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="shield"
            headline="Production-ready controls"
            subhead="Data is encrypted in transit and at rest. A Business Associate Agreement is available for production deployment."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about CuroSygnal™
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how the platform supports clinical teams while preserving
          clinician decision-making authority.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="Is CuroSygnal™ a medical device?"
          >
            CuroSygnal™ is positioned as a clinical decision support tool under
            the FDA Clinical Decision Support exemption, not Software as a
            Medical Device. It surfaces risk information and contributing
            clinical signals for licensed healthcare providers to independently
            review.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Does CuroSygnal™ replace clinical judgment?"
          >
            No. The platform supports clinician review by showing elevated risk
            indicators and the contributing signals behind each score. Final
            clinical decisions remain with the licensed clinician.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Does the demo use real patient data?"
          >
            No. Demo environments run on Curo46-hosted fake patients and
            facilities, so no PHI is exchanged during the sales cycle.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Which EHR platforms does CuroSygnal™ support?"
          >
            CuroSygnal™ is native to MatrixCare. Integration is available for
            other EHR platforms; <Link to="/contact-us">contact us</Link> to
            discuss your environment.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="curosygnal-final-cta">
        <Typography variant="h2" className="action-on-view action-fade-in">
          See CuroSygnal™ run on demo patients and facilities
        </Typography>
        <Typography className="action-on-view action-fade-in">
          The fastest way to evaluate the platform is a 30-minute working demo
          with a Curo46 product or sales lead.
        </Typography>
        <Button
          url="https://ggi.cal.com/curosygnal/curosygnal-30-minute-demo"
          label="Book a 30-minute demo"
          icon="calendar_month"
        />
      </Section>

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
